import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import { magazineApi } from '../../services/magazineApi';
import { TableEmptyRow, TableLoadingRow } from '../Common/PanelState';
import { useAuth } from '../../contexts/AuthContext';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const generateSlug = (title) => {
    if (!title) return '';
    return title.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
};

const formatPKR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-PK')}`;

const formatBytes = (n) => {
    const bytes = Number(n) || 0;
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
};

function IssueUploadOverlay({ percent, phase, coverFile, pdfFile, loaded, total }) {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    const uploading = phase !== 'processing';
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center border border-gray-100">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full border-[4px] border-rose-100" style={{ borderTopColor: '#c4151c', animation: 'flare-spin 0.8s linear infinite' }} />
                <h2 className="text-xl font-black text-gray-900">
                    {uploading ? 'Uploading magazine' : 'Saving magazine'}
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                    {uploading
                        ? 'Cover photo and PDF are being uploaded. Please do not close this page.'
                        : 'Files reached the server. Saving the issue now...'}
                </p>
                <div className="mt-6">
                    <div className="flex items-end justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Progress</span>
                        <span className="text-2xl font-black" style={{ color: '#c4151c' }}>{pct}%</span>
                    </div>
                    <div className="w-full h-3 rounded-full overflow-hidden bg-rose-50 border border-rose-100">
                        <div
                            className="h-full rounded-full transition-[width] duration-150 ease-out"
                            style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#c4151c,#ef4444)' }}
                        />
                    </div>
                    {total > 0 && (
                        <p className="mt-2 text-xs font-semibold text-gray-500">
                            {formatBytes(loaded)} / {formatBytes(total)}
                        </p>
                    )}
                </div>
                <div className="mt-5 text-left space-y-2">
                    {coverFile && (
                        <div className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            <span className="font-bold text-gray-600">Cover:</span> {coverFile.name} ({formatBytes(coverFile.size)})
                        </div>
                    )}
                    {pdfFile && (
                        <div className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            <span className="font-bold text-gray-600">PDF:</span> {pdfFile.name} ({formatBytes(pdfFile.size)})
                        </div>
                    )}
                </div>
            </div>
            <style>{`@keyframes flare-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_PHYSICAL_PRICE_PKR = 1500;

function nextIssueDefaultsFromIssues(existingIssues = []) {
    const now = new Date();
    let nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    const sorted = [...existingIssues]
        .filter(i => i.publication_date || i.month_year)
        .sort((a, b) => {
            const da = new Date(a.publication_date || (a.month_year ? new Date(`${a.month_year} 1`) : 0));
            const db = new Date(b.publication_date || (b.month_year ? new Date(`${b.month_year} 1`) : 0));
            return da - db;
        });
    if (sorted.length > 0) {
        const last = sorted[sorted.length - 1];
        const lastDate = new Date(last.publication_date || (last.month_year ? `${last.month_year} 1` : new Date(0)));
        if (Number.isFinite(lastDate.valueOf())) {
            const candidate = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 1);
            const nowCut = new Date(now.getFullYear(), now.getMonth(), 1);
            nextDate = candidate >= nowCut ? candidate : nowCut < new Date(nextDate) ? new Date(nextDate) : new Date(nextDate);
        }
    }
    const y = nextDate.getFullYear();
    const m = nextDate.getMonth();
    const vol = String(y - 2000);
    const issueNo = String(m + 1);
    const monthYear = `${MONTH_NAMES[m]} ${y}`;
    const title = `Flare Magazine ${monthYear}`;
    const pub = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    return { vol, issueNo, monthYear, title, pub, year: y, month: m };
}

function IssueForm({ issueToEdit, onBack, defaultContext = {} }) {
    const { user } = useAuth() || {};
    const adminEditorName = String(user?.name || user?.full_name || 'Ali Hassan').trim() || 'Ali Hassan';
    const nextDefaults = defaultContext.nextDefaults || nextIssueDefaultsFromIssues([]);
    const initialFormData = {
        title: nextDefaults.title,
        volume: nextDefaults.vol,
        issue_no: nextDefaults.issueNo,
        publication_date: nextDefaults.pub,
        month_year: nextDefaults.monthYear,
        status: 'draft',
        cover_image: null,
        pdf_file: null,
        cover_image_url: '',
        pdf_file_url: '',
        digital_price_pkr: 0,
        physical_price_pkr: DEFAULT_PHYSICAL_PRICE_PKR,
        is_physical_available: true,
        is_digital_free: true,
        preview_pages_count: 10,
        description: '',
        editor: adminEditorName,
        seo_title: '',
        seo_description: '',
        seo_keywords: '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const [coverFile, setCoverFile] = useState(null);
    const [fullPdfFile, setFullPdfFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(0);
    const [uploadPhase, setUploadPhase] = useState('upload');
    const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 });

    useEffect(() => {
        if (issueToEdit) {
            const keywords = issueToEdit.seo_keywords
                ? (Array.isArray(issueToEdit.seo_keywords) ? issueToEdit.seo_keywords.join(', ') : issueToEdit.seo_keywords)
                : '';
            setFormData({
                ...initialFormData,
                ...issueToEdit,
                editor: adminEditorName,
                is_digital_free: !!issueToEdit.is_digital_free,
                seo_keywords: keywords,
                cover_image_url: issueToEdit.cover_image_url || (magazineApi.getStorageUrl ? magazineApi.getStorageUrl(issueToEdit.cover_image) : ''),
                pdf_file_url: issueToEdit.pdf_file_url || (magazineApi.getStorageUrl ? magazineApi.getStorageUrl(issueToEdit.pdf_file) : ''),
            });
        } else {
            setFormData({
                ...initialFormData,
                editor: adminEditorName,
                seo_title: nextDefaults.title,
            });
        }
        setCoverFile(null);
        setFullPdfFile(null);
    }, [issueToEdit, nextDefaults.title, nextDefaults.vol, nextDefaults.issueNo, nextDefaults.monthYear, nextDefaults.pub, adminEditorName]);

    const handleFileChange = (e, setFile) => {
        const file = e.target.files[0];
        if (file) setFile(file);
    };

    const handleChange = (e) => {
        const { id, value, type, checked } = e.target;

        if (id === 'title') {
            setFormData(prevData => {
                const userEditedSeo = prevData.seo_title && prevData.seo_title !== prevData.title;
                return {
                    ...prevData,
                    title: value,
                    seo_title: userEditedSeo ? prevData.seo_title : value,
                };
            });
        } else if (id === 'publication_date' || id === 'month_year') {
            setFormData(prevData => {
                let next = { ...prevData, [id]: value };
                if (id === 'publication_date' && value) {
                    try {
                        const d = new Date(value);
                        if (Number.isFinite(d.valueOf())) {
                            const m = d.getMonth();
                            const y = d.getFullYear();
                            if (!issueToEdit) {
                                next.month_year = `${MONTH_NAMES[m]} ${y}`;
                                next.issue_no = String(m + 1);
                                next.volume = String(y - 2000);
                                if (!prevData.title || prevData.title === `Flare Magazine ${prevData.month_year}`) {
                                    next.title = `Flare Magazine ${next.month_year}`;
                                }
                                if (!prevData.seo_title || prevData.seo_title === prevData.title) {
                                    next.seo_title = next.title;
                                }
                            }
                        }
                    } catch {}
                }
                return next;
            });
        } else if (id === 'is_digital_free') {
            setFormData(prevData => ({
                ...prevData,
                is_digital_free: checked,
                digital_price_pkr: checked ? 0 : (prevData.digital_price_pkr || 299),
            }));
        } else if (id === 'digital_price_pkr' || id === 'physical_price_pkr' || id === 'preview_pages_count') {
            setFormData(prevData => ({
                ...prevData,
                [id]: Number(value) || 0,
                is_digital_free: id === 'digital_price_pkr' && Number(value) === 0 ? true : (id === 'digital_price_pkr' ? false : prevData.is_digital_free),
            }));
        } else if (id === 'editor') {
            // Editor always stays as logged-in admin name
            return;
        } else {
            setFormData(prevData => ({
                ...prevData,
                [id]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title) {
            toast.error("Title is required.");
            return;
        }
        if (!issueToEdit && (!coverFile || !fullPdfFile)) {
            toast.error("Cover image and PDF file are required for a new issue.");
            return;
        }
        const totalBytes = (coverFile?.size || 0) + (fullPdfFile?.size || 0);
        if ((coverFile?.size || 0) > 10 * 1024 * 1024) {
            toast.error('Cover image must be 10 MB or smaller.');
            return;
        }
        if ((fullPdfFile?.size || 0) > 100 * 1024 * 1024) {
            toast.error('PDF must be 100 MB or smaller.');
            return;
        }
        if (totalBytes > 110 * 1024 * 1024) {
            toast.error('Cover + PDF together must be under 110 MB.');
            return;
        }

        setIsSubmitting(true);
        setUploadPercent(0);
        setUploadPhase('upload');
        setUploadBytes({ loaded: 0, total: (coverFile?.size || 0) + (fullPdfFile?.size || 0) });

        try {
            const formDataObj = new FormData();

            formDataObj.append('title', formData.title);
            if (formData.volume) formDataObj.append('volume', formData.volume);
            if (formData.issue_no) formDataObj.append('issue_no', formData.issue_no);
            if (formData.publication_date) formDataObj.append('publication_date', formData.publication_date);
            if (formData.month_year) formDataObj.append('month_year', formData.month_year);
            formDataObj.append('status', formData.status);
            formDataObj.append('digital_price_pkr', formData.is_digital_free ? 0 : (formData.digital_price_pkr || 0));
            formDataObj.append('physical_price_pkr', formData.physical_price_pkr || 0);
            formDataObj.append('is_physical_available', formData.is_physical_available ? 1 : 0);
            formDataObj.append('is_digital_free', formData.is_digital_free ? 1 : 0);
            formDataObj.append('preview_pages_count', formData.preview_pages_count);
            if (formData.description) formDataObj.append('description', formData.description);
            if (adminEditorName) formDataObj.append('editor', adminEditorName);
            if (formData.seo_title) formDataObj.append('seo_title', formData.seo_title);
            if (formData.seo_description) formDataObj.append('seo_description', formData.seo_description);
            if (formData.seo_keywords) formDataObj.append('seo_keywords', formData.seo_keywords);

            if (coverFile) formDataObj.append('cover_image', coverFile);
            if (fullPdfFile) formDataObj.append('pdf_file', fullPdfFile);

            const uploadConfig = {
                onUploadProgress: (evt) => {
                    const total = evt.total || 0;
                    const loaded = evt.loaded || 0;
                    setUploadBytes({ loaded, total });
                    if (total > 0) {
                        const pct = Math.min(100, Math.round((loaded * 100) / total));
                        setUploadPercent(pct);
                        if (pct >= 100) setUploadPhase('processing');
                    }
                },
            };

            if (issueToEdit) {
                formDataObj.append('_method', 'PATCH');
                await magazineApi.updateIssue(issueToEdit.id, formDataObj, uploadConfig);
                toast.success("Magazine issue updated successfully!");
            } else {
                await magazineApi.createIssue(formDataObj, uploadConfig);
                toast.success("New magazine issue added successfully!");
            }
            onBack();
        } catch (error) {
            const status = error.response?.status;
            const errors = error.response?.data?.errors;
            if (errors) {
                Object.values(errors).flat().forEach(msg => toast.error(msg));
            } else if (status === 413) {
                toast.error('Upload is too large for the server. Use a PDF under 100 MB.');
            } else if (error.code === 'ECONNABORTED') {
                toast.error('Upload timed out. Check your connection and try again.');
            } else if (!error.response) {
                toast.error('Upload did not finish. The file may be too large or the connection dropped.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to save issue.');
            }
        } finally {
            setIsSubmitting(false);
            setUploadPercent(0);
            setUploadPhase('upload');
        }
    };

    return (
        <div className="container mx-auto">
            {isSubmitting && (
                <IssueUploadOverlay
                    percent={uploadPercent}
                    phase={uploadPhase}
                    coverFile={coverFile}
                    pdfFile={fullPdfFile}
                    loaded={uploadBytes.loaded}
                    total={uploadBytes.total}
                />
            )}
            <div className="mb-8">
                <button type="button" onClick={onBack} disabled={isSubmitting} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-40">Back to Issue List</button>
                <h1 className="text-4xl font-bold text-gray-800 mt-2">{issueToEdit ? 'Edit Magazine Issue' : 'Add New Magazine Issue'}</h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-md space-y-8">
                <div>
                    <h2 className="text-xl font-bold text-gray-700 border-b pb-3">Issue Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div><label htmlFor="title" className="font-medium">Title *</label><input type="text" id="title" value={formData.title} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                        <div><label htmlFor="status" className="font-medium">Status</label><select id="status" value={formData.status} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border"><option value="draft">Draft</option><option value="published">Published</option></select></div>
                        <div><label htmlFor="volume" className="font-medium">Volume</label><input type="text" id="volume" value={formData.volume} onChange={handleChange} placeholder="e.g. Vol. 5" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="issue_no" className="font-medium">Issue Number</label><input type="text" id="issue_no" value={formData.issue_no} onChange={handleChange} placeholder="e.g. Issue 12" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="publication_date" className="font-medium">Publication Date</label><input type="date" id="publication_date" value={formData.publication_date} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="month_year" className="font-medium">Month / Year Label</label><input type="text" id="month_year" value={formData.month_year} onChange={handleChange} placeholder="e.g. September 2026" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div>
                          <label htmlFor="editor" className="font-medium">Editor</label>
                          <input
                            type="text"
                            id="editor"
                            value={formData.editor || adminEditorName}
                            readOnly
                            className="mt-1 w-full p-3 bg-gray-100 rounded-lg border text-gray-700 cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Always set to the logged-in admin ({adminEditorName}).</p>
                        </div>
                    </div>
                </div>

                <div className="pt-6 border-t">
                    <h2 className="text-xl font-bold text-gray-700 mb-4">Pricing &amp; Access</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div>
                            <label htmlFor="digital_price_pkr" className="font-medium">Digital Price (PKR)</label>
                            <input type="number" id="digital_price_pkr" min="0" step="0.01" value={formData.digital_price_pkr} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" disabled={formData.is_digital_free} />
                            <p className="text-xs text-gray-500 mt-1">PDF single purchase price. Disable above to make it FREE.</p>
                        </div>
                        <div>
                            <label htmlFor="physical_price_pkr" className="font-medium">Physical Price (PKR)</label>
                            <input type="number" id="physical_price_pkr" min="0" step="0.01" value={formData.physical_price_pkr} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" disabled={!formData.is_physical_available} />
                            <p className="text-xs text-gray-500 mt-1">Hard copy + delivery extra.</p>
                        </div>
                        <div className="flex items-end">
                            <div className={`flex items-center gap-3 p-3 rounded-lg border w-full ${formData.is_digital_free ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50'}`}>
                                <input type="checkbox" id="is_digital_free" checked={formData.is_digital_free} onChange={handleChange} className="h-5 w-5 rounded" />
                                <label htmlFor="is_digital_free" className={`font-medium text-sm ${formData.is_digital_free ? 'text-emerald-800' : ''}`}>Make Digital Edition <span className="font-bold">FREE</span></label>
                            </div>
                        </div>
                        <div className="flex items-end">
                            <div className={`flex items-center gap-3 p-3 rounded-lg border w-full ${formData.is_physical_available ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50'}`}>
                                <input type="checkbox" id="is_physical_available" checked={formData.is_physical_available} onChange={handleChange} className="h-5 w-5 rounded" />
                                <label htmlFor="is_physical_available" className={`font-medium text-sm ${formData.is_physical_available ? 'text-indigo-800' : ''}`}>Sell Physical Copy</label>
                            </div>
                        </div>
                    </div>
                    {formData.is_digital_free && (
                        <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-start gap-3">
                            <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-5 w-5 mt-0.5 text-emerald-600" />
                            <div>
                                <div className="font-semibold">Website users will see <span className="uppercase">FREE DOWNLOAD</span> button for this issue.</div>
                                <div className="text-xs text-emerald-700 mt-0.5">Future price changes: uncheck this box and enter a price above.</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="pt-6 border-t">
                    <h2 className="text-xl font-bold text-gray-700 mb-4">Files & Preview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="font-medium">Cover Image *</label>
                            <input type="file" onChange={e => handleFileChange(e, setCoverFile)} className="mt-1 w-full p-2 border rounded-lg bg-gray-50" accept="image/*" required={!issueToEdit} />
                            {coverFile && (
                                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                                        <Icon path="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-4 w-4" />
                                        New cover selected
                                    </div>
                                    <div className="mt-1 text-xs text-emerald-800">
                                        <div><strong>File:</strong> {coverFile.name}</div>
                                        <div><strong>Size:</strong> {(coverFile.size / 1024 / 1024).toFixed(2)} MB</div>
                                    </div>
                                </div>
                            )}
                            {formData.cover_image_url && !coverFile && (
                                <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-indigo-700 font-semibold text-sm mb-2">
                                        <Icon path="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-4 w-4" />
                                        Current Cover
                                    </div>
                                    <img src={formData.cover_image_url} alt="cover preview" className="h-28 w-auto rounded border mb-2 object-contain bg-white" />
                                    <div className="text-xs text-indigo-800 space-y-0.5">
                                        <div><strong>File:</strong> {formData.cover_image ? formData.cover_image.split('/').pop() : '—'}</div>
                                        <a href={formData.cover_image_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline hover:text-blue-900">↗ Open cover in new tab</a>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="font-medium">Full Magazine PDF *</label>
                            <p className="text-xs text-gray-500 mt-0.5">Maximum 100 MB.</p>
                            <input type="file" onChange={e => handleFileChange(e, setFullPdfFile)} className="mt-1 w-full p-2 border rounded-lg bg-gray-50" accept="application/pdf" required={!issueToEdit} />
                            {fullPdfFile && (
                                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                                        <Icon path="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" className="h-4 w-4" />
                                        New PDF selected
                                    </div>
                                    <div className="mt-1 text-xs text-emerald-800">
                                        <div><strong>Name:</strong> {fullPdfFile.name}</div>
                                        <div><strong>Size:</strong> {(fullPdfFile.size / 1024 / 1024).toFixed(2)} MB</div>
                                    </div>
                                </div>
                            )}
                            {formData.pdf_file_url && !fullPdfFile && (
                                <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm mb-2">
                                        <Icon path="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-4 w-4" />
                                        Current PDF Attached
                                    </div>
                                    <div className="text-xs text-rose-900 bg-white border border-rose-100 rounded-md px-3 py-2 space-y-1">
                                        <div className="flex gap-2">
                                            <span className="font-semibold shrink-0 w-12">Name:</span>
                                            <span className="break-all font-mono">{formData.pdf_file ? formData.pdf_file.split('/').pop() : '—'}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="font-semibold shrink-0 w-12">Path:</span>
                                            <span className="break-all text-rose-700/80">{formData.pdf_file || '—'}</span>
                                        </div>
                                    </div>
                                    <a href={formData.pdf_file_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs bg-rose-600 hover:bg-rose-700 text-white font-semibold px-3 py-1.5 rounded-md shadow-sm transition-colors">
                                        <Icon path="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" className="h-3.5 w-3.5" />
                                        View PDF in new tab
                                    </a>
                                </div>
                            )}
                        </div>
                        <div>
                            <label htmlFor="preview_pages_count" className="font-medium">Preview Pages Count</label>
                            <input type="number" id="preview_pages_count" min="0" value={formData.preview_pages_count} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required />
                            <p className="text-xs text-gray-500 mt-1">Guest preview pages before buy prompt.</p>
                        </div>
                    </div>
                </div>

                <div className="pt-6 border-t">
                    <h2 className="text-xl font-bold text-gray-700 mb-4">SEO & Description</h2>
                    <div className="space-y-6">
                        <div><label htmlFor="description" className="font-medium">Description / Blurb</label><textarea id="description" value={formData.description} onChange={handleChange} rows="3" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="seo_title" className="font-medium">SEO Title</label><input type="text" id="seo_title" value={formData.seo_title} onChange={handleChange} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="seo_description" className="font-medium">SEO Meta Description</label><textarea id="seo_description" value={formData.seo_description} onChange={handleChange} rows="3" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                        <div><label htmlFor="seo_keywords" className="font-medium">Keywords (comma-separated)</label><input type="text" id="seo_keywords" value={formData.seo_keywords} onChange={handleChange} placeholder="fashion, lifestyle, pakistan" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end items-center pt-6 border-t gap-4">
                    <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto text-white font-bold py-3 px-10 rounded-lg shadow-lg disabled:opacity-50" style={{ backgroundColor: isSubmitting ? '#a11217' : '#c4151c' }}>
                        {isSubmitting ? `Uploading ${uploadPercent}%` : (issueToEdit ? 'Save Changes' : 'Add Issue')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function ManageIssuesSection() {
    const [issues, setIssues] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingIssue, setEditingIssue] = useState(null);
    const [deletingIssue, setDeletingIssue] = useState(null);
    const [view, setView] = useState('list');
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const currentYear = new Date().getFullYear();
    const [availableYears, setAvailableYears] = useState(() => {
        const ys = new Set([currentYear]);
        for (let y = currentYear - 1; y >= 2020; y--) ys.add(y);
        for (let y = currentYear + 1; y <= currentYear + 3; y++) ys.add(y);
        return Array.from(ys).sort((a, b) => b - a);
    });
    const [filters, setFilters] = useState({ status: '', search: '', year: String(currentYear) });
    const [page, setPage] = useState(1);

    const loadIssues = async (p = page, f = filters) => {
        setIsLoading(true);
        try {
            const params = { page: p, per_page: 100 };
            if (f.status) params.status = f.status;
            if (f.year) params.year = f.year;
            if (f.search) params.search = f.search;
            const { data } = await magazineApi.getIssues(params);
            const formatted = (data.data || []).map(i => magazineApi.formatIssue(i));
            const yearSet = new Set(availableYears);
            (data.data || []).forEach(i => {
                const pd = i.publication_date || i.month_year;
                if (pd) {
                    const ym = /(\d{4})/.exec(String(pd));
                    if (ym) yearSet.add(Number(ym[1]));
                }
            });
            setAvailableYears(Array.from(yearSet).sort((a, b) => b - a));
            setIssues(formatted);
            setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
        } catch (e) {
            toast.error('Failed to load issues.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadIssues(1, filters); }, [filters]);
    useEffect(() => { loadIssues(page, filters); }, [page]);

    const handleEdit = (issue) => { setEditingIssue(issue); setView('form'); };
    const handleAddNew = () => { setEditingIssue(null); setView('form'); };
    const nextDefaultsComputed = nextIssueDefaultsFromIssues(issues);

    const handleDelete = async () => {
        if (!deletingIssue) return;
        try {
            await magazineApi.deleteIssue(deletingIssue.id);
            toast.success(`Issue "${deletingIssue.title}" deleted successfully.`);
            loadIssues(pagination.current_page, filters);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete issue.');
        } finally {
            setDeletingIssue(null);
        }
    };

    if (view === 'form') return <IssueForm issueToEdit={editingIssue} onBack={() => setView('list')} defaultContext={{ nextDefaults: nextDefaultsComputed }} />;

    return (
        <div className="container mx-auto space-y-6">
            <ConfirmationModal isOpen={!!deletingIssue} onClose={() => setDeletingIssue(null)} onConfirm={handleDelete} title="Delete Magazine Issue">
                Are you sure you want to delete issue <strong>{deletingIssue?.title}</strong>? Files will be removed from server. This is irreversible.
            </ConfirmationModal>

            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div>
                    <h1 className="admin-page-title">Manage Magazine Issues</h1>
                    <p className="admin-page-sub">Upload, publish, and price your digital magazine issues.</p>
                </div>
                <button onClick={handleAddNew} className="text-white font-bold py-3 px-6 rounded-lg shadow-lg flex items-center gap-2" style={{ backgroundColor: '#c4151c' }}>
                    <Icon path="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <span>Add New Issue</span>
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-600">Status</label>
                        <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                            <option value="">All</option>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600">
                            Year <span className="text-rose-600">(Default: {currentYear})</span>
                        </label>
                        <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
                            <option value="">All Years</option>
                            {availableYears.map(y => (
                                <option key={y} value={String(y)}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-gray-600">Search by Title / Volume / Month</label>
                        <input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2" placeholder="Search issues..." />
                    </div>
                </div>
                {filters.year && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ backgroundColor: '#fff1f2', color: '#9f1016', border: '1px solid rgba(196,21,28,0.15)' }}>
                        <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-3.5 w-3.5" />
                        Filtered by year: <span className="underline">{filters.year}</span> · {pagination.total} total issue{pagination.total === 1 ? '' : 's'} matching
                        <button type="button" onClick={() => setFilters({ ...filters, year: '' })} className="ml-2 px-2 py-0.5 rounded-md bg-white border hover:bg-gray-50">
                            Clear
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left">Cover</th>
                                <th className="px-6 py-3 text-left">Title</th>
                                <th className="px-6 py-3 text-left">Issue</th>
                                <th className="px-6 py-3 text-left">Prices</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">PDF</th>
                                <th className="px-6 py-3 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {isLoading ? (<TableLoadingRow colSpan={7} label="Loading issues" />)
                                : issues.length > 0 ? issues.map(issue => (
                                    <tr key={issue.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4"><img src={issue.cover_image_url || 'https://placehold.co/60x80/E2E8F0/4A5568?text=No+Cover'} alt={issue.title} className="h-20 w-auto object-cover rounded-md shadow-sm" /></td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-800">{issue.title}</div>
                                            {issue.month_year && <div className="text-xs text-gray-500">{issue.month_year}</div>}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div>{issue.volume || '—'}</div>
                                            <div className="text-xs">{issue.issue_no || ''}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {issue.is_digital_free ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                    <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3 w-3" />
                                                    Digital FREE
                                                </span>
                                            ) : (
                                                <div className="text-xs text-gray-500">Digital: <span className="font-semibold text-gray-800">{formatPKR(issue.digital_price_pkr)}</span></div>
                                            )}
                                            {issue.is_physical_available && <div className="text-xs text-gray-500 mt-1">Physical: <span className="font-semibold text-gray-800">{formatPKR(issue.physical_price_pkr)}</span></div>}
                                        </td>
                                        <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${issue.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{issue.status}</span></td>
                                        <td className="px-6 py-4">{issue.pdf_file_url ? <a href={issue.pdf_file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">View PDF</a> : <span className="text-gray-400 text-xs">No PDF</span>}</td>
                                        <td className="px-6 py-4 text-center whitespace-nowrap">
                                            <button onClick={() => handleEdit(issue)} className="font-semibold text-sm bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">Edit</button>
                                            <span className="text-gray-300 mx-2">|</span>
                                            <button onClick={() => setDeletingIssue(issue)} className="font-semibold text-sm bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200">Delete</button>
                                        </td>
                                    </tr>
                                )) : (<TableEmptyRow colSpan={7} title="No magazine issues found" hint="Add a new issue to upload cover and PDF." />)}
                        </tbody>
                    </table>
                </div>
                {pagination.last_page > 1 && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
                        <div className="text-sm text-gray-600">Total {pagination.total} issues · Page {pagination.current_page} / {pagination.last_page}</div>
                        <div className="flex gap-2">
                            <button onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page <= 1} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Prev</button>
                            <button onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
